import React from 'react';
import Icon from '../../../components/AppIcon';
import SettingsSection from './SettingsSection';
import SettingItem from './SettingItem';
import ToggleSwitch from './ToggleSwitch';

/**
 * Enhanced privacy tab component for employer profile settings
 * Uses main profile data and saves through central save function
 */
const EmployerPrivacyTab = ({ profileData, onInputChange }) => {
  // Get privacy settings from main profile data or use defaults
  // Use nullish coalescing (??) to ensure all values are booleans, never undefined
  const privacySettings = {
    company_profile_visible: profileData?.privacy_settings?.company_profile_visible ?? true,
    contact_info_visible: profileData?.privacy_settings?.contact_info_visible ?? false,
    job_posts_visible: profileData?.privacy_settings?.job_posts_visible ?? true,
    company_size_visible: profileData?.privacy_settings?.company_size_visible ?? true,
    industry_visible: profileData?.privacy_settings?.industry_visible ?? true,
    benefits_visible: profileData?.privacy_settings?.benefits_visible ?? true,
    culture_visible: profileData?.privacy_settings?.culture_visible ?? true,
    allow_direct_contact: profileData?.privacy_settings?.allow_direct_contact ?? true,
    show_hiring_activity: profileData?.privacy_settings?.show_hiring_activity ?? true,
    allow_profile_indexing: profileData?.privacy_settings?.allow_profile_indexing ?? true,
    public_company_page: profileData?.privacy_settings?.public_company_page ?? false,
    hide_from_search: profileData?.privacy_settings?.hide_from_search ?? false,
    anonymous_browsing: profileData?.privacy_settings?.anonymous_browsing ?? false,
    allow_salary_disclosure: profileData?.privacy_settings?.allow_salary_disclosure ?? false
  };
  
  // Handle privacy setting changes
  const handleToggleChange = (setting, value) => {
    const newSettings = {
      ...privacySettings,
      [setting]: value
    };
    
    // Update main profile data
    onInputChange('privacy_settings', newSettings);
  };

  return (
    <div className="space-y-6">
      {/* Company Profile Visibility */}
      <SettingsSection
        title="Company Profile Visibility"
        description="Control what information is visible to VAs and other users"
        icon="Eye"
      >
        <div className="space-y-4">
          <SettingItem
            title="Make Company Profile Public"
            description="Allow your company profile to be discovered by VAs and candidates"
            control={
              <ToggleSwitch
                checked={privacySettings.company_profile_visible}
                onChange={(checked) => handleToggleChange('company_profile_visible', checked)}
              />
            }
          />

          <SettingItem
            title="Show Contact Information"
            description="Display your company contact details to verified VAs"
            control={
              <ToggleSwitch
                checked={privacySettings.contact_info_visible}
                onChange={(checked) => handleToggleChange('contact_info_visible', checked)}
              />
            }
          />

          <SettingItem
            title="Show Job Posts"
            description="Display your current and past job postings"
            control={
              <ToggleSwitch
                checked={privacySettings.job_posts_visible}
                onChange={(checked) => handleToggleChange('job_posts_visible', checked)}
              />
            }
          />

          <SettingItem
            title="Show Company Size"
            description="Display the number of employees in your company"
            control={
              <ToggleSwitch
                checked={privacySettings.company_size_visible}
                onChange={(checked) => handleToggleChange('company_size_visible', checked)}
              />
            }
          />

          <SettingItem
            title="Show Industry Information"
            description="Display your company's industry and business focus"
            control={
              <ToggleSwitch
                checked={privacySettings.industry_visible}
                onChange={(checked) => handleToggleChange('industry_visible', checked)}
              />
            }
          />

          <SettingItem
            title="Show Benefits & Perks"
            description="Display the benefits and perks you offer to employees"
            control={
              <ToggleSwitch
                checked={privacySettings.benefits_visible}
                onChange={(checked) => handleToggleChange('benefits_visible', checked)}
              />
            }
          />

          <SettingItem
            title="Show Company Culture"
            description="Display information about your company culture and values"
            control={
              <ToggleSwitch
                checked={privacySettings.culture_visible}
                onChange={(checked) => handleToggleChange('culture_visible', checked)}
              />
            }
          />
        </div>
      </SettingsSection>

      {/* Communication Settings */}
      <SettingsSection
        title="Communication & Contact"
        description="Manage how VAs and candidates can contact your company"
        icon="MessageCircle"
      >
        <div className="space-y-4">
          <SettingItem
            title="Allow Direct Contact"
            description="Let VAs and candidates contact you directly through the platform"
            control={
              <ToggleSwitch
                checked={privacySettings.allow_direct_contact}
                onChange={(checked) => handleToggleChange('allow_direct_contact', checked)}
              />
            }
          />

          <SettingItem
            title="Show Hiring Activity"
            description="Display your recent hiring activity and open positions"
            control={
              <ToggleSwitch 
                checked={privacySettings.show_hiring_activity}
                onChange={(checked) => handleToggleChange('show_hiring_activity', checked)}
              />
            }
          />
        </div>
      </SettingsSection>

      {/* Search & Discovery */}
      <SettingsSection
        title="Search & Discovery"
        description="Control how your company appears in search results and recommendations"
        icon="Search"
      >
        <div className="space-y-4">
          <SettingItem
            title="Allow Search Engine Indexing"
            description="Let search engines like Google index your public company profile"
            control={
              <ToggleSwitch
                checked={privacySettings.allow_profile_indexing}
                onChange={(checked) => handleToggleChange('allow_profile_indexing', checked)}
              />
            }
          />

          <SettingItem
            title="Public Company Page"
            description="Allow anyone with the link to view your company page without signing up"
            control={
              <ToggleSwitch
                checked={privacySettings.public_company_page}
                onChange={(checked) => handleToggleChange('public_company_page', checked)}
              />
            }
          />

          <SettingItem
            title="Hide from Internal Search"
            description="Don't show your company in VA Sprouts internal search results"
            control={
              <ToggleSwitch
                checked={privacySettings.hide_from_search}
                onChange={(checked) => handleToggleChange('hide_from_search', checked)}
              />
            }
          />

          <SettingItem
            title="Anonymous Profile Browsing"
            description="Don't show your company name when viewing VA profiles (shows as 'Anonymous Employer')"
            control={
              <ToggleSwitch
                checked={privacySettings.anonymous_browsing}
                onChange={(checked) => handleToggleChange('anonymous_browsing', checked)}
              />
            }
          />
        </div>
      </SettingsSection>

      {/* Compensation & Benefits */}
      <SettingsSection
        title="Compensation & Benefits"
        description="Control the display of salary and benefit information"
        icon="DollarSign"
      >
        <div className="space-y-4">
          <SettingItem
            title="Allow Salary Disclosure"
            description="Show salary ranges in job postings and company profile"
            control={
              <ToggleSwitch
                checked={privacySettings.allow_salary_disclosure}
                onChange={(checked) => handleToggleChange('allow_salary_disclosure', checked)}
              />
            }
          />
        </div>
      </SettingsSection>

      {/* Privacy Notice */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="space-y-2">
            <h4 className="font-medium text-text-primary">Privacy & Data Protection</h4>
            <div className="text-sm text-text-secondary space-y-2">
              <p>
                Your company's privacy is important to us. We follow strict data protection guidelines and never share your information without your consent.
              </p>
              <ul className="list-disc list-inside space-y-1 pl-4">
                <li>Privacy settings apply immediately</li>
                <li>Contact information is only shared with verified VAs</li>
                <li>You can update these settings anytime</li>
                <li>Some information may still be visible to platform administrators for safety</li>
                <li>Hiding your company from search may reduce candidate applications</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployerPrivacyTab;